import {
  ClipboardEvent,
  FocusEvent,
  FormEvent,
  KeyboardEvent,
  MouseEvent,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { cn } from 'src/shared/lib/utils';

interface EditFieldProps {
  value?: string;
  onChange: (_value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onInput?: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}

const EditField = forwardRef<HTMLDivElement, EditFieldProps>(
  ({ value, onChange, onFocus, onBlur, onInput, onKeyDown, placeholder, autoFocus = false, className }, ref) => {
    const [showPlaceholder, setShowPlaceholder] = useState(!value);
    const [isFocused, setIsFocused] = useState(false);
    const elementRef = useRef<HTMLDivElement>(null);

    // Use the forwarded ref or fallback to internal ref
    const divRef = ref || elementRef;

    useEffect(() => {
      setShowPlaceholder(!value && !isFocused);
    }, [value, isFocused]);

    // Auto-focus effect
    useEffect(() => {
      if (divRef && typeof divRef !== 'function' && divRef.current && autoFocus) {
        divRef.current.focus();
        // place cursor at the end if there's content
        if (value) {
          const range = document.createRange();
          const sel = window.getSelection();
          range.selectNodeContents(divRef.current);
          range.collapse(false);
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
      }
    }, [autoFocus, value, divRef]);

    function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
      onKeyDown?.(e);

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.blur();
        onBlur?.();
        e.currentTarget.textContent = '';
        return;
      }

      if (e.key === 'Escape') {
        e.currentTarget.textContent = value || '';
        e.currentTarget.blur();
        onBlur?.();
        e.currentTarget.textContent = '';
        return;
      }
    }

    function handleFocus(e: FocusEvent<HTMLDivElement>) {
      e.stopPropagation();
      setIsFocused(true);
      setShowPlaceholder(false);
      onFocus?.();
    }

    function handleBlur(e: FocusEvent<HTMLDivElement, Element>) {
      setIsFocused(false);
      const newValue = e.currentTarget.textContent || '';
      if (newValue !== value) {
        onChange(newValue);
      }
      setShowPlaceholder(!newValue);
      e.currentTarget.textContent = '';
      onBlur?.();
    }

    function handleInput(e: FormEvent<HTMLDivElement>) {
      const target = e.currentTarget;
      target.style.height = 'auto';

      if (isFocused) {
        setShowPlaceholder(false);
      }
      onInput?.();
    }

    function handlePaste(e: ClipboardEvent<HTMLDivElement>) {
      e.preventDefault();

      const text = e.clipboardData.getData('text/plain');

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);

        // move cursor to end of inserted text
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        selection.removeAllRanges();
        selection.addRange(range);

        // trigger input event to update state
        const inputEvent = new Event('input', { bubbles: true });
        e.currentTarget.dispatchEvent(inputEvent);
      }
    }

    // prevent parent click events from interfering
    function handleClick(e: MouseEvent<HTMLDivElement>) {
      e.stopPropagation();
      if (!isFocused && divRef && typeof divRef !== 'function' && divRef.current) {
        divRef.current.focus();
      }
    }

    return (
      <div
        contentEditable
        suppressContentEditableWarning={true}
        data-placeholder={placeholder}
        data-gramm="false"
        data-gramm_editor="false"
        data-enable-grammarly="false"
        spellCheck="false"
        tabIndex={0}
        className={cn(
          'input bg-neutral-100 rounded-sm outline-none px-1.5 block resize-none overflow-hidden w-auto break-word',
          showPlaceholder &&
            placeholder &&
            'before:content-[attr(data-placeholder)] before:text-gray-400 before:pointer-events-none',
          className
        )}
        ref={divRef}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onPaste={handlePaste}
        onClick={handleClick}
      >
        {value}
      </div>
    );
  }
);

EditField.displayName = 'EditField';

export default EditField;
